import React, { useState } from 'react';
import {
  FaLightbulb, FaBullseye, FaChartLine, FaShieldAlt,
  FaCheckCircle, FaChevronDown, FaGraduationCap,
  FaHome, FaFireAlt, FaUmbrellaBeach, FaBuilding
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
const FinancialPlanning = () => {
  // State for Accordion FAQ toggling
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      q: "What is Financial Planning and why is it important?",
      a: "Financial planning is a comprehensive evaluation of your current and future financial state by using known variables to predict cash flows, asset values, and withdrawal plans. It is crucial because it provides a clear roadmap to achieve life goals like buying a home, funding children's education, and retiring comfortably without financial stress."
    },
    {
      q: "What is FIRE Planning (Financial Independence, Retire Early)?",
      a: "FIRE planning is an intensive strategy focused on aggressive saving and disciplined investing during your working years. The goal is to accumulate a portfolio large enough to generate passive income that covers your annual living expenses, allowing you to retire decades earlier than traditional retirement age."
    },
    {
      q: "How does goal-based investing differ from regular investing?",
      a: "Goal-based investing links every investment you make to a specific life objective with a defined timeline and target amount (e.g., higher education in 10 years, retirement in 20 years). This approach dictates the exact asset allocation and risk profile required, rather than chasing random market returns."
    },
    {
      q: "How often should my financial plan be reviewed?",
      a: "A financial plan should be reviewed at least once a year, or immediately following major life milestones such as marriage, the birth of a child, a career change, buying property, or significant shifts in market economic conditions."
    }
  ];

  return (
    <main className="financial-planning-page">
      <Helmet>
        {/* Basic SEO */}
        <title>Financial Planning Services in Hyderabad | Certified Financial Advisors | Lotoswealth</title>

        <meta
          name="description"
          content="Lotoswealth provides expert Financial Planning Services in Hyderabad, helping individuals, families, professionals, and business owners achieve their financial goals through personalized investment planning, wealth management, retirement planning, tax planning, insurance planning, and goal-based financial strategies."
        />

        <meta
          name="keywords"
          content="Financial Planning Services Hyderabad, Financial Planning Hyderabad, Financial Advisor Hyderabad, Certified Financial Planner Hyderabad, CFP Hyderabad, Wealth Management Hyderabad, Investment Planning Hyderabad, Goal Based Financial Planning Hyderabad, Retirement Planning Hyderabad, Tax Planning Hyderabad, Insurance Planning Hyderabad, Estate Planning Hyderabad, Wealth Creation Hyderabad, Asset Management Hyderabad, Personal Financial Planning Hyderabad, Family Financial Planning Hyderabad, SIP Investment Hyderabad, Mutual Funds Hyderabad, Investment Advisor Hyderabad, Lotoswealth"
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Lotoswealth" />
        <meta name="language" content="English" />

        <link rel="canonical" href="https://www.lotoswealth.com/financial-planning" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Lotoswealth" />
        <meta property="og:url" content="https://www.lotoswealth.com/financial-planning" />
        <meta
          property="og:title"
          content="Financial Planning Services in Hyderabad | Lotoswealth"
        />
        <meta
          property="og:description"
          content="Plan your financial future with Lotoswealth. Expert Financial Planning Services in Hyderabad including investment planning, retirement planning, tax planning, insurance planning, and wealth management."
        />
        <meta
          property="og:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Financial Planning Services in Hyderabad | Lotoswealth"
        />
        <meta
          name="twitter:description"
          content="Achieve your financial goals with expert Financial Planning Services in Hyderabad. Personalized investment planning, retirement planning, tax planning, wealth management, and financial advisory."
        />
        <meta
          name="twitter:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />
      </Helmet>
      {/* =========================================
          1. HERO SECTION (With Ticker & Slow Zoom)
      ========================================= */}
      <div className="hero-wrapper position-relative" style={{ height: '100vh', minHeight: '800px', overflow: 'hidden' }}>

        <div className="position-absolute top-0 start-0 w-100 h-100 z-0 animate-slow-zoom">
          <img
            src="/images/financial-planning.jpg"
            alt="Financial Planning Services"
            className="w-100 h-100 object-fit-cover"
          />
        </div>

        {/* Slanted Panels Overlay */}
        <div className="panels-container position-absolute top-0 start-0 w-100 h-100 z-1 d-flex">
          <div className="slanted-panel panel-1"></div>
          <div className="slanted-panel panel-2"></div>
          <div className="slanted-panel panel-3"></div>
          <div className="slanted-panel panel-4"></div>
        </div>

        {/* --- Floating Main Header Text (Perfectly Centered) --- */}
        <div className="container hero-main-text text-center position-absolute top-50 start-50 translate-middle z-3 w-100 px-4">
          <h1 className="fw-bold display-4 animate-slide-up delay-1 mb-3 text-white" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
            Comprehensive Financial Planning
          </h1>
          <h2 className="animate-slide-up delay-2 fw-bold text-white mb-4" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Your Strategic Roadmap to Lifelong Financial Freedom
          </h2>
          <p className="hero-description animate-slide-up delay-3 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Welcome to Lotoswealth. Financial success does not happen by chance—it is the result of deliberate planning, structured goal setting, and disciplined execution. Our financial planning solutions are designed to guide you through every stage of life with absolute confidence.
          </p>
          <p className="hero-description mt-3 animate-slide-up delay-4 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Whether you are planning for your dream home, your children's higher education, early retirement (FIRE), or post-retirement income stability, our experienced advisors construct tailored roadmaps that align your income, savings, and investments with your personal aspirations.
          </p>
          <p className="hero-description mt-3 animate-slide-up delay-5 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Take control of your financial destiny with clear projections, intelligent asset allocation, and dedicated advisory support every step of the way.
          </p>
        </div>

        {/* --- Scrolling Stock Ticker --- */}
        <div className="stock-ticker position-absolute bottom-0 w-100 z-3 bg-dark text-white border-top border-bottom border-secondary py-2" style={{ opacity: 0.9 }}>
          <div className="ticker-track">
            {/* First Set */}
            <div className="ticker-item text-white border-end border-secondary"><strong>APOLLOHOSP</strong> 8,894.00 <span className="text-danger">-63.00 (-0.70%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>ASIANPAINT</strong> 2,768.60 <span className="text-success">21.30 (0.78%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>AXISBANK</strong> 1,250.00 <span className="text-success">20.50 (1.67%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJ-AUTO</strong> 11,519.00 <span className="text-danger">-2.00 (-0.02%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJFINSV</strong> 2,085.60 <span className="text-success">56.50 (2.78%)</span></div>

            {/* Duplicated Set for Infinite Scrolling */}
            <div className="ticker-item text-white border-end border-secondary"><strong>APOLLOHOSP</strong> 8,894.00 <span className="text-danger">-63.00 (-0.70%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>ASIANPAINT</strong> 2,768.60 <span className="text-success">21.30 (0.78%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>AXISBANK</strong> 1,250.00 <span className="text-success">20.50 (1.67%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJ-AUTO</strong> 11,519.00 <span className="text-danger">-2.00 (-0.02%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJFINSV</strong> 2,085.60 <span className="text-success">56.50 (2.78%)</span></div>
          </div>
        </div>
      </div>

      {/* =========================================
          2. WHY FINANCIAL PLANNING MATTERS (Soft Teal Theme)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f0f9f6' }}>
        <div className="container py-4">
          <div className="row align-items-center g-5">
            <div className="col-lg-6 animate-slide-up delay-1">
              <h2 className="fw-bold mb-4 text-dark">
                Why Financial Planning is Crucial
              </h2>
              <p className="text-muted line-height-lg">
                Many individuals make the mistake of saving whatever money is left after expenses, rather than spending what is left after structured saving. Without a financial plan, life milestones can feel overwhelming and uncertain.
              </p>
              <p className="text-muted line-height-lg">
                Financial planning bridges the gap between where you are today and where you want to be tomorrow. It brings clarity to your cash flow, optimizes your tax efficiency, builds safety nets for emergencies, and ensures your money works as hard as you do.
              </p>
            </div>
            <div className="col-lg-6 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm border-start border-teal border-5">
                <h4 className="fw-bold text-teal mb-3">Core Pillars of Our Planning</h4>
                <ul className="list-unstyled text-muted mb-0">
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Clarity & Direction:</strong> Clear target amounts and timelines for every goal.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Risk Mitigation:</strong> Adequate insurance and emergency fund buffers.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Inflation Defiance:</strong> Growing wealth faster than the rising cost of living.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Peace of Mind:</strong> Eliminating financial anxiety through organized structures.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          3. OUR FINANCIAL PLANNING SOLUTIONS (Crisp White)
      ========================================= */}
      <section className="py-5 bg-white" style={{ backgroundImage: "url('/images/lotoswealth-bg.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}>

        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-white">📋 Our Financial Planning Solutions</h2>
            <p className="text-white">Customized strategies designed to prepare you for every stage of life.</p>
          </div>

          <div className="row g-4">

            <div className="col-lg-4 col-md-6 animate-slide-up delay-1">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaBullseye size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Goal-Based Investing</h5>
                <p className="text-muted small">Mapping investments directly to life milestones such as purchasing a home, funding children's education, or planning a luxury vacation with precise asset allocation.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-2">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaUmbrellaBeach size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Retirement & Post-Retirement</h5>
                <p className="text-muted small">Building a robust retirement corpus during your earning years and structuring steady, tax-efficient post-retirement cash flows to sustain your lifestyle.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-3">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaFireAlt size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">FIRE Planning</h5>
                <p className="text-muted small">Financial Independence, Retire Early (FIRE) strategies tailored for high-savers seeking aggressive wealth accumulation and early freedom from traditional work.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-4">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaBuilding size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Corporate Financial Wellness</h5>
                <p className="text-muted small">Specialized programs for organizations to improve employee financial literacy, stress management, and long-term personal wealth retention.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-5">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaHome size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Major Life Milestones</h5>
                <p className="text-muted small">Advisory support for buying your first home, planning weddings, managing business expansions, and setting up emergency contingency funds.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-6">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaShieldAlt size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Wealth Transfer & Contingency</h5>
                <p className="text-muted small">Safeguarding your family against unforeseen life events with comprehensive risk profiling, life insurance planning, and multi-generational wealth transfer.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          4. OUR 5-STEP PLANNING PROCESS (Dark Slate)
      ========================================= */}
      <section className="py-5 text-white" style={{ backgroundColor: '#0a192f' }}>
        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-white">🔄 Our Financial Planning Process</h2>
            <p className="text-light opacity-75">A structured, 5-stage roadmap designed to bring total financial clarity.</p>
          </div>

          <div className="row g-4">

            <div className="col-md-6 col-lg-4 animate-slide-up delay-1">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">1️⃣ Discover</h5>
                <p className="small text-muted mb-0">We understand your financial goals, income sources, existing investments, liabilities, and future aspirations through an in-depth consultation.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4 animate-slide-up delay-2">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">2️⃣ Analyze</h5>
                <p className="small text-muted mb-0">Our experts assess your current financial standing, cash flow patterns, risk appetite, and identify gaps or growth opportunities.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4 animate-slide-up delay-3">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">3️⃣ Strategize</h5>
                <p className="small text-muted mb-0">We craft a comprehensive, customized financial roadmap tailored specifically to your milestones, complete with projected timelines.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-6 animate-slide-up delay-4">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">4️⃣ Implement</h5>
                <p className="small text-muted mb-0">We recommend and execute suitable investment instruments, wealth management strategies, insurance policies, and loan solutions.</p>
              </div>
            </div>

            <div className="col-md-12 col-lg-6 animate-slide-up delay-5">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">5️⃣ Monitor</h5>
                <p className="small text-muted mb-0">Conducting regular portfolio reviews and milestone checks to ensure your investments remain aligned with your evolving life goals.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          5. WHO WE SERVE & WHY CHOOSE US (Soft Blue-Gray)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#eef2f5' }}>
        <div className="container py-4">
          <div className="row g-5">

            {/* Target Audience */}
            <div className="col-lg-5 animate-slide-up delay-1">
              <h3 className="fw-bold mb-4 text-dark">👥 Who We Help Plan For</h3>
              <p className="text-muted small mb-4">Our financial planning solutions support clients across all walks of life:</p>
              <div className="d-flex flex-wrap gap-2">
                {[
                  "Buying a Dream Home", "Children's Higher Education",
                  "Marriage Planning", "Business Expansion",
                  "Building Emergency Funds", "Ensuring Retirement Income",
                  "Seamless Wealth Transfer", "Achieving FIRE (Early Retirement)"
                ].map((item, idx) => (
                  <span key={idx} className="badge bg-white text-dark border p-2 shadow-sm fs-6">
                    {item}
                  </span>
                ))}
              </div>
            </div>

            {/* Why Choose Lotoswealth */}
            <div className="col-lg-7 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm h-100">
                <h3 className="fw-bold mb-3 text-dark">🏆 Why Choose Lotoswealth?</h3>
                <p className="text-muted small mb-4">Building a secure financial future requires trusted guidance and expertise. Clients choose us because we offer:</p>
                <div className="row g-3">
                  {[
                    "Personalized Financial Roadmaps", "Goal-Based Disciplined Investing",
                    "Comprehensive Wealth Management", "Retirement & Legacy Planning",
                    "Client-First Advisory Approach", "Transparent Guidance & No Hidden Fees",
                    "Dedicated Relationship Support", "Long-Term Wealth Creation Focus"
                  ].map((item, idx) => (
                    <div key={idx} className="col-md-6 d-flex align-items-center">
                      <FaCheckCircle className="text-success me-2 flex-shrink-0" />
                      <span className="small text-muted fw-bold">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          6. FREQUENTLY ASKED QUESTIONS (FAQ)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container py-4" style={{ maxWidth: '850px' }}>
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-dark">❓ Frequently Asked Questions</h2>
            <p className="text-muted">Common queries regarding Financial Planning & Goal Management</p>
          </div>

          <div className="d-flex flex-column gap-3 animate-slide-up delay-2">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-3 shadow-sm border overflow-hidden"
              >
                <button
                  className="w-100 text-start p-3 border-0 bg-white d-flex justify-content-between align-items-center fw-bold text-dark"
                  onClick={() => toggleFaq(index)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="pe-3">{faq.q}</span>
                  <FaChevronDown
                    className={`text-teal transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                    style={{ transition: 'transform 0.3s ease', minWidth: '16px' }}
                  />
                </button>
                {openFaq === index && (
                  <div className="p-3 pt-0 text-muted small border-top bg-light lh-lg">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          7. FINAL CTA SECTION (Deep Navy)
      ========================================= */}
      <section className="py-5 text-white text-center" style={{ backgroundColor: '#0f1e2d' }}>
        <div className="container py-4">
          <h2 className="fw-bold mb-3 animate-slide-up delay-1">Your Financial Journey Starts Here</h2>
          <p className="text-light opacity-75 mb-4 animate-slide-up delay-2 mx-auto" style={{ maxWidth: '600px' }}>
            Grow. Protect. Prosper. Let Lotoswealth help you build, manage, and secure your financial future with confidence.
          </p>
          <button className="btn-custom-teal hover-lift px-5 py-3 fs-5 border-0 rounded text-white shadow animate-slide-up delay-3">
            Schedule a Financial Planning Consultation
          </button>
        </div>
      </section>

    </main>
  );
};

export default FinancialPlanning;